// src/components/CustomerFlow/Steps/PreSignatureConfirmationStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCreditCard, 
  faCheckCircle, 
  faExternalLinkAlt, 
  faExclamationTriangle,
  faMoneyBill,
  faMoneyBillTransfer
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../services/api';
import { formatCurrency } from '../../../utils/numberFormatter';

const MySwal = withReactContent(Swal);

const PreSignatureConfirmationStep = ({ 
  flowData, 
  stepStatuses,
  onNext,
  searchParams
}) => {
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contractLoading, setContractLoading] = useState(false);
  const [error, setError] = useState(null);


  // Estados para Signatura (de SignatureStep)
  const [signatureData, setSignatureData] = useState({
    signature_url: null,
    testing_url: null
  });
  const [signatureError, setSignatureError] = useState(null);
  const [isReturningFromExternal, setIsReturningFromExternal] = useState(false);
  const [hasProcessedExternalReturn, setHasProcessedExternalReturn] = useState(false);
  const [isInitiatingSignature, setIsInitiatingSignature] = useState(false);
  
  
  // Estado para mostrar todos los métodos de pago
  const [showAllPaymentMethods, setShowAllPaymentMethods] = useState(false);
  

  // Función para limpiar el parámetro from_step de la URL
  const cleanUrlParameter = () => {
    const fromStep = searchParams?.get('from_step');
    
    // Limpiar si viene de signature o identity_validation
    if (fromStep === 'signature' || fromStep === 'identity_validation') {
      const url = new URL(window.location.href);
      url.searchParams.delete('from_step');
      
      // Actualizar la URL sin el parámetro, sin recargar la página
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['PreSignatureConfirmationStep'];
    
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      
      onNext({
        step_name: 'PreSignatureConfirmationStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Verificar si venimos del servicio externo de firma
  useEffect(() => {
    const fromStep = searchParams?.get('from_step');
    const currentStepStatus = stepStatuses['PreSignatureConfirmationStep'];
    
    const isReturningFromSignature = (fromStep === 'identity_validation') 
      && currentStepStatus === 'in_progress' 
      && !hasProcessedExternalReturn;
    
    if (isReturningFromSignature) {
      setIsReturningFromExternal(true);
      setHasProcessedExternalReturn(true);
      
      // Limpiar el parámetro de la URL inmediatamente
      cleanUrlParameter();
      
      handleExternalSignatureComplete();
    }
  }, [searchParams, stepStatuses, hasProcessedExternalReturn]);

  // Cargar datos de la solicitud de solicitud
  useEffect(() => {
    if (flowData?.tokenCustomerId && flowData?.tokenRequestId && !isReturningFromExternal) {
      fetchRequestData();
    }
  }, [flowData, isReturningFromExternal]);

  // Generar contrato cuando se cargan los datos de la solicitud
  useEffect(() => {
    if (requestData && !contractData && !isReturningFromExternal) {
      generateContract();
    }
  }, [requestData, contractData, isReturningFromExternal]);

  const fetchRequestData = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}`
      );
      setRequestData(response.data);
    } catch (error) {
      console.error('Error fetching request data:', error);
      setError('Error al cargar los datos de la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const generateContract = async () => {
    try {
      setContractLoading(true);
      const response = await api.post(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}/generate-contract`
      );
      
      setContractData(response.data.contract);
    } catch (error) {
      console.error('Error generating contract:', error);
      setError('Error al generar el contrato. Por favor, inténtalo nuevamente.');
    } finally {
      setContractLoading(false);
    }
  };

  // Obtener el enlace de firma de Signatura
  const fetchSignatureLink = async () => {
    try {
      setSignatureError(null);

      const response = await api.get(
        `/customers/${flowData.tokenCustomerId}/requests/${flowData.tokenRequestId}/self-service/identity-validation`
      );
      
      if (response.data.success) {
        const { verification_url, testing_url } = response.data.data;
        
        // Retornar ambos valores para que handleValidateAndSign los use
        return {
          verification_url: verification_url || null,
          testing_url: testing_url || null
        };
      } else {
        throw new Error('No se pudo obtener el enlace de firma');
      }
    } catch (error) {
      console.error('Error fetching signature link:', error);
      
      // Intentar extraer testing_url del error si está disponible
      const errorData = error.response?.data?.data;
      const testingUrl = errorData?.testing_url || null;
      
      return {
        verification_url: null,
        testing_url: testingUrl,
        error: error.response?.data?.message || 'No se pudo obtener el enlace de firma'
      };
    }
  };

  // Manejar la completación de la firma externa
  const handleExternalSignatureComplete = async () => {
    try {
      // Mostrar el mensaje de éxito por 3 segundos
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // TODO: Refactorizar esto ya que es poco eficiente y genera rigidez en el proceso.
      // Determinar el estado según el tipo de flujo
      // Si es flujo no_biometric (sin biometría), usar 'completed'
      // Si es flujo standard (con biometría), usar 'processed'
      const FLOW_TYPE_NO_BIOMETRIC = 'no_biometric';
      const flowType = flowData?.flow_type || 'standard';
      const requiresManualReview = flowType === FLOW_TYPE_NO_BIOMETRIC;
      const stepStatus = requiresManualReview ? 'completed' : 'processed';
      
      // Marcar el paso según el tipo de flujo
      onNext({ 
        step_name: 'PreSignatureConfirmationStep',
        step_status: stepStatus,
        external_signature_completed: true,
        completion_timestamp: new Date().toISOString(),
        completed_from_external: true,
        flow_type: flowType
      }, stepStatus);
      
    } catch (error) {
      console.error('Error procesando la firma externa:', error);
      setSignatureError('Error al completar la firma. Por favor, recarga la página.');
    }
  };

  // Iniciar proceso de validación de identidad y firma
  const handleValidateAndSign = async () => {
    if (isInitiatingSignature) return;

    try {
      setIsInitiatingSignature(true);
      setSignatureError(null);

      // Obtener URLs de validación
      const result = await fetchSignatureLink();

      // Si tenemos verification_url, redirigir inmediatamente
      if (result.verification_url) {
        // Marcar el paso como in_progress antes de redirigir
        onNext({ 
          step_name: 'PreSignatureConfirmationStep',
          step_status: 'in_progress',
          external_signature_started: true,
          signature_timestamp: new Date().toISOString(),
          signature_link: result.verification_url
        }, 'in_progress');
        
        // Redirigir al usuario
        window.location.href = result.verification_url;
        return;
      }

      // Si llegamos aquí, hubo un error
      setIsInitiatingSignature(false);
      
      // Verificar si tenemos testing_url disponible
      if (result.testing_url) {
        // Mostrar modal con opción de reintentar o continuar sin validación
        const swalResult = await MySwal.fire({
          icon: 'error',
          title: 'No pudimos iniciar la validación de identidad',
          text: 'Podés intentar nuevamente o continuar sin validación',
          showCancelButton: true,
          confirmButtonText: 'Reintentar',
          cancelButtonText: 'Continuar sin validación',
          confirmButtonColor: '#435ebe',
          cancelButtonColor: '#6c757d'
        });

        if (swalResult.isConfirmed) {
          // Reintentar
          handleValidateAndSign();
        } else if (swalResult.dismiss === Swal.DismissReason.cancel) {
          // Continuar sin validación - redirigir a testing_url
          onNext({ 
            step_name: 'PreSignatureConfirmationStep',
            step_status: 'in_progress',
            external_signature_started: true,
            signature_timestamp: new Date().toISOString(),
            signature_link: result.testing_url,
            testing_mode: true
          }, 'in_progress');
          
          window.location.href = result.testing_url;
        }
      } else {
        // Error sin testing_url - solo opción de reintentar
        const swalResult = await MySwal.fire({
          icon: 'error',
          title: 'No pudimos iniciar la validación de identidad',
          text: 'Podés intentar nuevamente',
          confirmButtonText: 'Reintentar',
          confirmButtonColor: '#435ebe'
        });

        if (swalResult.isConfirmed) {
          // Reintentar
          handleValidateAndSign();
        }
      }
    } catch (error) {
      console.error('Error al iniciar firma:', error);
      setIsInitiatingSignature(false);
      
      // Error inesperado
      await MySwal.fire({
        icon: 'error',
        title: 'Error inesperado',
        text: 'Ocurrió un error al iniciar el proceso de firma. Por favor, intenta nuevamente.',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#435ebe'
      });
    }
  };


  // Función para formatear el número de cuotas
  const formatInstallments = () => {
    if (!requestData?.financial_plan) return 'N/A';
    const { installments, installment_amount } = requestData.financial_plan;
    return `${installments} de ${formatCurrency(installment_amount)}`;
  };

  // Si estamos retornando del servicio externo, mostrar mensaje de completado
  if (isReturningFromExternal) {
    return (
      <div className="pre-signature-confirmation-step pre-signature-confirmation-step--centered">
        <div className="pre-signature-confirmation-step__status">
          <FontAwesomeIcon icon={faCheckCircle} className="pre-signature-confirmation-step__status-icon pre-signature-confirmation-step__status-icon--success" />
          <h3 className="pre-signature-confirmation-step__success-title">¡Firma Completada!</h3>
          <p className="pre-signature-confirmation-step__lead">
            Tu identidad ha sido validada y el documento ha sido firmado exitosamente. 
            Estamos procesando la información...
          </p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Procesando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pre-signature-confirmation-step pre-signature-confirmation-step--centered">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="pre-signature-confirmation-step__lead">Cargando detalles de la solicitud...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pre-signature-confirmation-step pre-signature-confirmation-step--centered">
        <div className="alert alert-light-danger">
          <p className="mb-0">{error}</p>
          <button 
            className="rg-btn rg-btn--outline-danger rg-btn--sm pre-signature-confirmation-step__retry-btn"
            onClick={() => {
              setError(null);
              if (requestData) {
                generateContract();
              } else {
                fetchRequestData();
              }
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!requestData) {
    return (
      <div className="pre-signature-confirmation-step pre-signature-confirmation-step--centered">
        <div className="alert alert-warning">
          <p className="mb-0">No se pudieron cargar los datos de la solicitud</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pre-signature-confirmation-step">
      {/* Header */}
      <div className="pre-signature-confirmation-step__header">
        <h3 className="pre-signature-confirmation-step__title">Revisá los datos antes de firmar</h3>
        <p className="pre-signature-confirmation-step__subtitle">Este es el resumen final de tu solicitud.</p>
      </div>

      <div className="step-body">
        {/* Summary Card - Información Financiera Destacada */}
        <section className="pre-signature-confirmation-step__summary-card" aria-label="Condiciones principales">
          <h4 className="pre-signature-confirmation-step__summary-title">Condiciones principales</h4>

          {/* Monto total */}
          <div className="pre-signature-confirmation-step__summary-row">
            <span className="pre-signature-confirmation-step__summary-label">Monto total</span>
            <strong className="pre-signature-confirmation-step__summary-value pre-signature-confirmation-step__summary-value--amount">
              {formatCurrency(requestData.financial_plan?.original_amount || requestData.amount)}
            </strong>
          </div>

          {/* Cuotas */}
          <div className="pre-signature-confirmation-step__summary-row">
            <span className="pre-signature-confirmation-step__summary-label">Plan de pago</span>
            <strong className="pre-signature-confirmation-step__summary-value">
              {formatInstallments()}
            </strong>
          </div>

          {/* Primera cuota vence */}
          <div className="pre-signature-confirmation-step__summary-row">
            <span className="pre-signature-confirmation-step__summary-label">Primer vencimiento</span>
            <span className="pre-signature-confirmation-step__summary-date">
              {requestData.financial_plan?.display_date_formatted}
            </span>
          </div>
        </section>


        {/* Métodos de pago con iconos */}
        <section className="pre-signature-confirmation-step__payment-methods" aria-label="Formas de pago aceptadas">
          <p className="pre-signature-confirmation-step__methods-title">Formas de pago aceptadas</p>
          <div className="pre-signature-confirmation-step__methods-grid">
            <span className="pre-signature-confirmation-step__method-chip">
              <FontAwesomeIcon icon={faMoneyBill} className="pre-signature-confirmation-step__method-icon" />
              RapiPago
            </span>
            <span className="pre-signature-confirmation-step__method-chip">
              <FontAwesomeIcon icon={faMoneyBill} className="pre-signature-confirmation-step__method-icon" />
              Pago Fácil
            </span>
            <span className="pre-signature-confirmation-step__method-chip">
              <FontAwesomeIcon icon={faCreditCard} className="pre-signature-confirmation-step__method-icon" />
              MercadoPago
            </span>
            {showAllPaymentMethods && (
              <>
                <span className="pre-signature-confirmation-step__method-chip">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} className="pre-signature-confirmation-step__method-icon" />
                  Cobro Express
                </span>
                <span className="pre-signature-confirmation-step__method-chip">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} className="pre-signature-confirmation-step__method-icon" />
                  Transferencia
                </span>
              </>
            )}
          </div>
          {!showAllPaymentMethods && (
            <div className="pre-signature-confirmation-step__methods-link-wrap">
              <button
                type="button"
                className="rg-btn rg-btn--link pre-signature-confirmation-step__methods-link"
                onClick={() => setShowAllPaymentMethods(true)}
              >
                Ver todos los métodos
              </button>
            </div>
          )}
        </section>


        {/* Error de Signatura */}
        {signatureError && (
          <div className="alert alert-light-danger mb-4">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {signatureError}
          </div>
        )}

        {/* Botón principal - CTA */}
        <div className="pre-signature-confirmation-step__cta">
          <button
            className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full pre-signature-confirmation-step__cta-btn"
            onClick={handleValidateAndSign}
            disabled={!contractData || contractLoading || isInitiatingSignature}
          >
            {isInitiatingSignature ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Procesando validación...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                Continuar con la firma
              </>
            )}
          </button>

          {/* Mensaje de procesamiento */}
          {isInitiatingSignature && (
            <p className="pre-signature-confirmation-step__processing-note">
              Estamos preparando la validación de identidad
            </p>
          )}

          {/* Enlace de testing discreto (solo visible en QA) */}
          {signatureData.testing_url && !isInitiatingSignature && (
            <div className="pre-signature-confirmation-step__qa-link-wrap">
              <a 
                href={signatureData.testing_url} 
                rel="noopener noreferrer" 
                className="pre-signature-confirmation-step__qa-link"
              >
                Saltar a paso de redirección (QA)
              </a>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default PreSignatureConfirmationStep;

